/**
 * Which currency we bill a person in, and how we guess it before they say.
 *
 * ── Why this is not `preferences.defaultCurrency` ───────────────────────────
 *
 * That preference answers a different question: which currency the installer
 * prices THEIR OWN work in, on quotes and bills of materials. It accepts all
 * 178 codes in `currency.ts`, because a South African installer quotes in ZAR
 * whether or not we can charge them in it.
 *
 * Billing is constrained by what actually exists as a `currency_options` entry
 * on the Stripe prices. Offering a currency we cannot charge would put a
 * number on the pricing page that the payment page then contradicts — the
 * exact failure this whole feature exists to remove. So the user makes ONE
 * choice and we narrow it here, rather than storing a second preference that
 * could drift out of step with the first.
 */

import { getCurrencyMeta } from './currency';

/**
 * The currencies every Stripe price carries. Kept in sync with
 * `scripts/gen-currency-matrix.mjs`; `scripts/check-price-parity.mjs` fails the
 * build if Stripe and this list disagree.
 */
export const BILLING_CURRENCIES = [
  'AED', 'AUD', 'BRL', 'CAD', 'CHF', 'CZK', 'DKK', 'EUR', 'GBP', 'INR',
  'JPY', 'MXN', 'NOK', 'PLN', 'SAR', 'SEK', 'TRY', 'USD', 'ZAR',
] as const;

export type BillingCurrency = (typeof BILLING_CURRENCIES)[number];

/** Everything we cannot charge in falls back here. */
export const FALLBACK_BILLING_CURRENCY: BillingCurrency = 'EUR';

const BILLING_SET = new Set<string>(BILLING_CURRENCIES);

export function isBillingCurrency(code: string | null | undefined): code is BillingCurrency {
  return !!code && BILLING_SET.has(code.toUpperCase());
}

/**
 * Narrow any stored preference to something we can actually charge.
 * A South African installer keeps quoting in ZAR (which we now support) but a
 * Kenyan one, quoting in KES, is billed in euro rather than shown a price that
 * Stripe would refuse.
 */
export function toBillingCurrency(code: string | null | undefined): BillingCurrency {
  return isBillingCurrency(code) ? (code.toUpperCase() as BillingCurrency) : FALLBACK_BILLING_CURRENCY;
}

/**
 * Region subtag -> currency, limited to what we can charge. Only regions whose
 * currency we support appear; anything else deliberately falls through to euro
 * instead of guessing a neighbouring country's money.
 */
const REGION_TO_CURRENCY: Record<string, BillingCurrency> = {
  // Eurozone
  AT: 'EUR', BE: 'EUR', CY: 'EUR', DE: 'EUR', EE: 'EUR', ES: 'EUR', FI: 'EUR',
  FR: 'EUR', GR: 'EUR', HR: 'EUR', IE: 'EUR', IT: 'EUR', LT: 'EUR', LU: 'EUR',
  LV: 'EUR', MT: 'EUR', NL: 'EUR', PT: 'EUR', SI: 'EUR', SK: 'EUR',
  // Rest of Europe
  GB: 'GBP', PL: 'PLN', CZ: 'CZK', SE: 'SEK', NO: 'NOK', DK: 'DKK',
  CH: 'CHF', LI: 'CHF', TR: 'TRY',
  // Americas
  US: 'USD', CA: 'CAD', BR: 'BRL', MX: 'MXN',
  // Asia-Pacific, Middle East, Africa.
  // No NZ entry on purpose: New Zealand dollars are not a Stripe presentment
  // currency here, and quoting a New Zealander in Australian dollars would be
  // its own small insult on top of being the wrong number. They get euro like
  // every other unsupported market.
  AU: 'AUD', JP: 'JPY', IN: 'INR',
  AE: 'AED', SA: 'SAR', ZA: 'ZAR',
};

/**
 * Best guess from a BCP 47 tag such as `en-GB`, `pt-BR` or `de`.
 *
 * Reads the REGION, never the language: `en` alone says nothing about who
 * pays in what, and guessing USD from `en` would hand a Briton dollars.
 * A tag without a region therefore yields euro, the same as an unknown one.
 */
export function currencyFromLocale(locale: string | null | undefined): BillingCurrency {
  if (!locale) return FALLBACK_BILLING_CURRENCY;
  // Intl.Locale resolves shorthands (`en-US-POSIX`, `zh-Hant-TW`) properly;
  // the manual split covers engines that lack it.
  let region: string | undefined;
  try {
    region = new Intl.Locale(locale).region ?? undefined;
  } catch {
    region = undefined;
  }
  if (!region) {
    // Positional, not by value: the region is a subtag AFTER the language, so
    // `parts.slice(1)` says that directly. Matching on "two letters, not equal
    // to parts[0]" instead threw away `pl_PL` (region equals the language) and
    // demanded upper case that a tag like `en_gb` does not carry.
    const parts = locale.split(/[-_]/);
    region = parts.slice(1).find((p) => /^[A-Za-z]{2}$/.test(p));
  }
  if (!region) return FALLBACK_BILLING_CURRENCY;
  return REGION_TO_CURRENCY[region.toUpperCase()] ?? FALLBACK_BILLING_CURRENCY;
}

/** Detect from the browser, falling back to euro when there is nothing to read. */
export function detectBillingCurrency(): BillingCurrency {
  // `window`, not `navigator`. Node ships a global navigator whose language is
  // "en-US", so a `typeof navigator` check passes during the static prerender
  // and every prerendered page "detected" the United States: /pl/pricing was
  // built quoting dollars, and the FAQ and offer schema Google reads carried
  // them too. A browser is the only place this question has an answer.
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return FALLBACK_BILLING_CURRENCY;
  }
  const tags = [navigator.language, ...(navigator.languages ?? [])].filter(Boolean) as string[];
  for (const tag of tags) {
    const cur = currencyFromLocale(tag);
    // The first tag carrying a region we recognise wins. A euro result from a
    // region-less tag must not stop the search, or `en` would mask `en-CA`.
    if (cur !== FALLBACK_BILLING_CURRENCY) return cur;
    try {
      if (new Intl.Locale(tag).region) return cur;
    } catch { /* keep looking */ }
  }
  return FALLBACK_BILLING_CURRENCY;
}

/**
 * Whether this person's currency is fixed by Stripe and must not be offered
 * as a choice.
 *
 * Stripe anchors a customer to the first currency they are invoiced in and
 * refuses any other afterwards ("You cannot combine currencies on a single
 * customer"), so a switcher shown to an existing payer would promise something
 * the checkout then rejects. Verified against the live API on 2026-08-19.
 */
export function isCurrencyLocked(subscription: {
  stripe_customer_id?: string | null;
  plan?: string | null;
  status?: string | null;
} | null | undefined): boolean {
  if (!subscription?.stripe_customer_id) return false;
  const plan = subscription.plan;
  // A free row carries a customer id only because we create one early at
  // checkout; nothing has been invoiced yet, so nothing is anchored.
  if (!plan || plan === 'free' || plan === 'trial') return false;
  // Cancelled subscribers stay anchored in Stripe as long as the customer
  // exists, so they cannot be handed a switcher either.
  return true;
}

/** Picker entries, ordered so the common ones are not buried under the rest. */
export function billingCurrencyOptions(): Array<{ code: BillingCurrency; label: string }> {
  const priority: BillingCurrency[] = ['EUR', 'USD', 'GBP', 'PLN'];
  const ordered = [...priority, ...BILLING_CURRENCIES.filter((c) => !priority.includes(c))];
  return ordered.map((code) => ({ code, label: `${code} ${getCurrencyMeta(code).symbol}` }));
}

/**
 * The currency a checkout should be created in, computed from the source of
 * truth (the saved preference) rather than the displayCurrency STATE.
 *
 * Why not just read displayCurrency: the pricing page's auto-checkout effect can
 * fire in the same render in which preferences resolve, before the effect that
 * copies the saved currency into displayCurrency state has been applied. In that
 * render displayCurrency still holds the browser-detected guess (EUR for an
 * unrecognised locale), so an MXN customer's auto-checkout went to Stripe in
 * euro. `preferences` is current in that render's closure; displayCurrency is a
 * render behind. So the auto path passes the result of THIS function as an
 * explicit override, and never depends on the state having caught up.
 *
 * The rule mirrors the displayCurrency effect exactly: a saved choice wins only
 * once onboarding is complete AND a currency is set; otherwise fall back to
 * whatever the page is already showing (browser detection, euro if unrecognised).
 * A manual card click passes no override and keeps reading displayCurrency,
 * which by click time is fully settled.
 */
export function resolveCheckoutCurrency(
  hasUser: boolean,
  preferences: { onboardingCompleted?: boolean; defaultCurrency?: string | null } | null | undefined,
  displayCurrency: string,
): string {
  if (hasUser && preferences?.onboardingCompleted && preferences.defaultCurrency) {
    return toBillingCurrency(preferences.defaultCurrency);
  }
  return displayCurrency;
}
